import React, { ButtonHTMLAttributes, forwardRef, ReactNode } from "react";
import { LoaderCircle } from "lucide-react";
import styles from "./MGButton.module.css";

export type TMGButtonAppearance =
  | "primary"
  | "secondary"
  | "tertiary"
  | "variant"
  | "success"
  | "destructive";
export type TMGButtonSize = "sm" | "lg";
export type TMGButtonBackground = "light" | "dark" | "brand";

export type TMGButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  appearance?: TMGButtonAppearance;
  size?: TMGButtonSize;
  background?: TMGButtonBackground;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  dataAction?: string;
  dataLayer?: string;
  dataTestId?: string;
  fullWidth?: boolean;
  loading?: boolean;
  loadingLabel?: string;
};

export const MGButton = forwardRef<HTMLButtonElement, TMGButtonProps>(
  (
    {
      appearance = "primary",
      size = "lg",
      background = "light",
      iconLeft,
      iconRight,
      dataAction,
      dataLayer,
      dataTestId,
      fullWidth = false,
      loading = false,
      loadingLabel = "A processar",
      disabled = false,
      type = "button",
      className,
      children,
      "aria-label": ariaLabel,
      "aria-busy": ariaBusy,
      ...buttonProps
    },
    ref,
  ) => {
    const hasLabel = React.Children.toArray(children).length > 0;

    return (
      <button
        {...buttonProps}
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={`${styles.button}${className ? ` ${className}` : ""}`}
        data-appearance={appearance}
        data-size={size}
        data-background={background}
        data-full-width={fullWidth}
        data-icon-only={!hasLabel}
        data-loading={loading}
        data-action={dataAction}
        data-layer={dataLayer}
        data-testid={dataTestId}
        aria-label={loading ? loadingLabel : ariaLabel}
        aria-busy={loading || ariaBusy}>
        <span className={styles.content} aria-hidden={loading || undefined}>
          {iconLeft && (
            <span
              className={styles.icon}
              aria-hidden="true"
              data-testid={dataTestId && `${dataTestId}-icon-left`}>
              {iconLeft}
            </span>
          )}
          {hasLabel && <span className={styles.label}>{children}</span>}
          {iconRight && (
            <span
              className={styles.icon}
              aria-hidden="true"
              data-testid={dataTestId && `${dataTestId}-icon-right`}>
              {iconRight}
            </span>
          )}
        </span>
        {loading && <LoaderCircle className={styles.spinner} aria-hidden="true" />}
      </button>
    );
  },
);

MGButton.displayName = "MGButton";
